import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useMember, memberApi } from "@/contexts/MemberAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MissionsWidget() {
  const { member } = useMember();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    if (!member) return;
    try {
      const { data } = await memberApi.get("/missions/today");
      setData(data);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [member?.id]);

  const complete = async (slug) => {
    setBusy(slug);
    try {
      const { data: r } = await memberApi.post(`/missions/${slug}/complete`);
      if (r.already_completed) toast.info("Already completed today");
      else toast.success(`+${r.points_awarded} pts`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  if (!data) return null;
  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-6 mb-8" data-testid="missions-widget">
      <div className="flex justify-between items-center mb-4">
        <p className="text-[10px] uppercase tracking-widest text-white/40">Daily Missions · {data.completed}/{data.total}</p>
        <div className="w-24 h-1.5 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#8B0000]" style={{ width: `${(data.completed / Math.max(data.total, 1)) * 100}%` }} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {(data.missions || []).map((m) => (
          <div
            key={m.slug}
            data-testid={`mission-${m.slug}`}
            className={`flex justify-between items-center p-3 rounded border transition-colors ${
              m.completed ? "border-[#D4AF37]/30 bg-[#D4AF37]/5" : "border-white/5 hover:border-white/20"
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{m.icon || "◆"}</span>
              <div>
                <p className={`text-sm ${m.completed ? "text-[#D4AF37]" : "text-white"}`}>{m.name}</p>
                <p className="text-white/40 text-[10px]">+{m.points} pts</p>
              </div>
            </div>
            {m.completed ? (
              <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
            ) : (
              <button
                onClick={() => complete(m.slug)}
                disabled={busy === m.slug}
                data-testid={`mission-complete-${m.slug}`}
                className="text-[10px] uppercase tracking-widest text-white/50 hover:text-[#D4AF37] px-2 py-1"
              >
                Claim
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReferralWidget() {
  const [data, setData] = useState(null);
  useEffect(() => { memberApi.get("/member/referrals").then((r) => setData(r.data)); }, []);
  if (!data) return null;

  const copy = () => {
    navigator.clipboard.writeText(data.referral_link);
    toast.success("Referral link copied");
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-6 mb-8" data-testid="referral-widget">
      <div className="flex justify-between items-center mb-4">
        <p className="text-[10px] uppercase tracking-widest text-white/40">Your Referral Link</p>
        <div className="flex gap-4 text-xs">
          <div className="text-white/40">Invited: <span className="text-white">{data.referrals_count}</span></div>
          <div className="text-white/40">Revenue: <span className="text-[#D4AF37]">${data.referral_revenue.toFixed(2)}</span></div>
          <div className="text-white/40">Pts earned: <span className="text-[#D4AF37]">{data.referral_earnings_points}</span></div>
        </div>
      </div>
      <div className="flex gap-2">
        <input
          readOnly value={data.referral_link}
          data-testid="referral-link-input"
          className="flex-1 bg-black border border-white/10 px-3 py-2 text-white text-xs font-mono rounded"
        />
        <button onClick={copy} data-testid="referral-copy-btn" className="text-xs bg-[#D4AF37] text-black px-4 py-2 hover:bg-white uppercase tracking-widest flex items-center gap-2">
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
      </div>
      <p className="text-white/30 text-[11px] mt-3">
        Earn +100 pts every time someone you invite makes their first purchase. Points scale up as you climb tiers.
      </p>
    </div>
  );
}

export function RewardsPanel() {
  const [rewards, setRewards] = useState(null);
  useEffect(() => { memberApi.get("/member/rewards").then((r) => setRewards(r.data)); }, []);
  if (!rewards) return null;

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-6 mb-8" data-testid="rewards-panel">
      <div className="flex justify-between items-center mb-4">
        <p className="text-[10px] uppercase tracking-widest text-white/40">Rewards</p>
        <p className="text-xs text-[#D4AF37]">{rewards.points} points</p>
      </div>

      {rewards.promo_codes.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="active-promo-codes">
          <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Active Promo Codes</p>
          {rewards.promo_codes.map((p, i) => (
            <div key={i} className="flex justify-between items-center bg-black border border-[#D4AF37]/40 rounded px-3 py-2">
              <div>
                <div className="text-white text-sm">{p.name}</div>
                <div className="text-[#D4AF37] font-mono text-xs">{p.code}</div>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(p.code); toast.success("Code copied"); }} className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-white">Copy</button>
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-2">
        {[...rewards.earned.map((r) => ({ ...r, earned: true })), ...rewards.available.map((r) => ({ ...r, earned: false }))].map((r) => (
          <div
            key={r.slug}
            data-testid={`reward-${r.slug}`}
            className={`p-3 rounded border ${r.earned ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/5"}`}
          >
            <div className="flex justify-between items-start mb-1">
              <p className={`text-sm font-medium ${r.earned ? "text-[#D4AF37]" : "text-white/70"}`}>
                {r.earned ? "✓ " : ""}{r.name}
              </p>
              <span className="text-[10px] text-white/40">{r.points_required} pts</span>
            </div>
            <p className="text-white/40 text-[11px]">{r.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
